import React, { useState, useCallback, useRef } from 'react';
import { NovaCoin3D } from './NovaCoin3D.js';
import { FloatingSpatialCoins } from './FloatingSpatialCoins.js';

export interface MiningReactorProps {
  size?: number; // Reactor diameter in px (default 220)
  progressPercent?: number; // 0 - 100 for current cycle
  ratePerSecond?: number;
  status?: 'mining' | 'loading' | 'claiming' | 'claimed' | 'failed' | 'idle' | 'paused';
  variant?: 'gold' | 'cyan' | 'purple' | 'multicolor';
  className?: string;
  showCoin?: boolean;
  children?: React.ReactNode;
}

export const MiningReactor: React.FC<MiningReactorProps> = ({
  size = 220,
  progressPercent = 0,
  ratePerSecond = 0,
  status = 'mining',
  variant = 'gold',
  className = '',
  showCoin = true,
  children,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const strokeWidth = Math.max(6, Math.round(size * 0.042));
  const radius = (size - strokeWidth * 2) / 2;
  const circumference = 2 * Math.PI * radius;
  const safePercent = Math.min(100, Math.max(0, progressPercent));
  const strokeDashoffset = circumference - (safePercent / 100) * circumference;

  // Calculate tip particle coordinates on the radial arc
  const tipAngleDeg = (safePercent / 100) * 360 - 90;
  const tipAngleRad = (tipAngleDeg * Math.PI) / 180;
  const tipX = size / 2 + radius * Math.cos(tipAngleRad);
  const tipY = size / 2 + radius * Math.sin(tipAngleRad);

  // Desktop Pointer Parallax Handler
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e.clientX - cx) / (rect.width / 2);
    const dy = (e.clientY - cy) / (rect.height / 2);
    setTilt({
      x: Math.max(-12, Math.min(12, -dy * 12)),
      y: Math.max(-12, Math.min(12, dx * 12)),
    });
  }, []);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    setTilt({ x: 0, y: 0 });
  }, []);

  // Variant color definitions
  const colorMap = {
    gold: {
      primary: '#f59e0b',
      secondary: '#fbbf24',
      ambient: 'radial-gradient(circle, rgba(245, 158, 11, 0.35) 0%, rgba(124, 58, 237, 0.2) 45%, transparent 75%)',
      orbit1: 'rgba(251, 191, 36, 0.75)',
      orbit2: 'rgba(6, 182, 212, 0.65)',
      orbit3: 'rgba(236, 72, 153, 0.45)',
      nodeColor: '#fef08a',
      gradId: 'reactorGoldGrad',
      glowDrop: 'rgba(245, 158, 11, 0.55)',
    },
    cyan: {
      primary: '#06b6d4',
      secondary: '#22d3ee',
      ambient: 'radial-gradient(circle, rgba(6, 182, 212, 0.35) 0%, rgba(99, 102, 241, 0.2) 45%, transparent 75%)',
      orbit1: 'rgba(34, 211, 238, 0.75)',
      orbit2: 'rgba(168, 85, 247, 0.65)',
      orbit3: 'rgba(245, 158, 11, 0.45)',
      nodeColor: '#a5f3fc',
      gradId: 'reactorCyanGrad',
      glowDrop: 'rgba(6, 182, 212, 0.55)',
    },
    purple: {
      primary: '#a855f7',
      secondary: '#c084fc',
      ambient: 'radial-gradient(circle, rgba(168, 85, 247, 0.35) 0%, rgba(6, 182, 212, 0.2) 45%, transparent 75%)',
      orbit1: 'rgba(192, 132, 252, 0.75)',
      orbit2: 'rgba(251, 191, 36, 0.65)',
      orbit3: 'rgba(34, 211, 238, 0.45)',
      nodeColor: '#e9d5ff',
      gradId: 'reactorPurpleGrad',
      glowDrop: 'rgba(168, 85, 247, 0.55)',
    },
    multicolor: {
      primary: '#ffd700',
      secondary: '#00f2fe',
      ambient: 'radial-gradient(circle, rgba(168, 85, 247, 0.32) 0%, rgba(0, 242, 254, 0.22) 50%, transparent 75%)',
      orbit1: 'rgba(0, 242, 254, 0.8)',
      orbit2: 'rgba(236, 72, 153, 0.7)',
      orbit3: 'rgba(255, 215, 0, 0.5)',
      nodeColor: '#ffffff',
      gradId: 'reactorMultiGrad',
      glowDrop: 'rgba(0, 242, 254, 0.6)',
    },
  };

  const c = colorMap[variant];

  // Dynamic coin sizing
  const coinSize = size >= 210 ? 'xl' : size >= 140 ? 'lg' : size >= 80 ? 'md' : 'sm';

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`relative flex items-center justify-center select-none ${className}`}
      style={{
        width: size,
        height: size,
        perspective: 900,
      }}
    >
      {/* 0. Ambient Floating Spatial Micro-Coins */}
      {size >= 180 && <FloatingSpatialCoins />}

      {/* 1. Deep Atmospheric Reactor Chamber Glow */}
      <div
        className="absolute inset-2 rounded-full pointer-events-none transition-all duration-700"
        style={{
          background:
            status === 'claimed'
              ? 'radial-gradient(circle, rgba(16, 185, 129, 0.45) 0%, transparent 75%)'
              : status === 'failed'
              ? 'radial-gradient(circle, rgba(239, 68, 68, 0.45) 0%, transparent 75%)'
              : c.ambient,
          filter: 'blur(28px)',
          transform: 'scale(1.15)',
        }}
      />

      {/* 2. Spatial 3D Energy Orbit 1 (Tilted 64° on X, Clockwise Spin) */}
      <div
        className="absolute inset-3 pointer-events-none"
        style={{
          transformStyle: 'preserve-3d',
          transform: 'rotateX(64deg) rotateY(16deg)',
        }}
      >
        <div
          className="w-full h-full rounded-full border border-dashed spatial-orbit-1 relative"
          style={{
            borderColor: c.orbit1,
            borderWidth: 1.5,
            borderSpacing: '4px',
          }}
        >
          {/* Glowing Flux Node on Orbit 1 */}
          <div
            className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full"
            style={{
              backgroundColor: c.secondary,
              boxShadow: `0 0 10px ${c.secondary}, 0 0 20px ${c.primary}`,
            }}
          />
        </div>
      </div>

      {/* 3. Spatial 3D Energy Orbit 2 (Tilted 70° on X, Counter-Clockwise Spin) */}
      <div
        className="absolute inset-5 pointer-events-none"
        style={{
          transformStyle: 'preserve-3d',
          transform: 'rotateX(68deg) rotateY(-22deg)',
        }}
      >
        <div
          className="w-full h-full rounded-full border spatial-orbit-2 relative opacity-60"
          style={{
            borderColor: c.orbit2,
            borderWidth: 1.2,
          }}
        >
          {/* Luminous Quantum Node on Orbit 2 */}
          <div
            className="absolute -bottom-1 left-1/4 w-2.5 h-2.5 rounded-full"
            style={{
              backgroundColor: '#00f2fe',
              boxShadow: '0 0 8px #00f2fe, 0 0 16px rgba(0,242,254,0.6)',
            }}
          />
        </div>
      </div>

      {/* 4. Spatial Equatorial Energy Ring (Dashed Gold Ring) */}
      <div
        className="absolute inset-1 pointer-events-none"
        style={{
          transformStyle: 'preserve-3d',
          transform: 'rotateX(82deg)',
        }}
      >
        <div
          className="w-full h-full rounded-full border border-dashed spatial-orbit-3 opacity-40"
          style={{
            borderColor: c.orbit3,
            borderWidth: 1,
          }}
        />
      </div>

      {/* 5. Authoritative SVG Progress Chamber */}
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="absolute inset-0 pointer-events-none z-10"
      >
        <defs>
          <linearGradient id="reactorGoldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fffbeb" />
            <stop offset="25%" stopColor="#fef08a" />
            <stop offset="60%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#b45309" />
          </linearGradient>
          <linearGradient id="reactorCyanGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ecfeff" />
            <stop offset="30%" stopColor="#a5f3fc" />
            <stop offset="65%" stopColor="#06b6d4" />
            <stop offset="100%" stopColor="#0284c7" />
          </linearGradient>
          <linearGradient id="reactorPurpleGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#faf5ff" />
            <stop offset="30%" stopColor="#e9d5ff" />
            <stop offset="65%" stopColor="#a855f7" />
            <stop offset="100%" stopColor="#6d28d9" />
          </linearGradient>
          <linearGradient id="reactorMultiGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ffd700" />
            <stop offset="45%" stopColor="#ec4899" />
            <stop offset="100%" stopColor="#00f2fe" />
          </linearGradient>
        </defs>

        {/* Base Track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="rgba(255, 255, 255, 0.08)"
          strokeWidth={strokeWidth}
          fill="none"
        />

        {/* Dynamic Accrual Progress Arc */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={`url(#${c.gradId})`}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          fill="none"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{
            transition: 'stroke-dashoffset 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
            filter: `drop-shadow(0 0 8px ${c.glowDrop})`,
          }}
        />

        {/* Radiant Leading Edge Head Node (only if progress > 1%) */}
        {safePercent > 1 && safePercent < 99 && (
          <circle
            cx={tipX}
            cy={tipY}
            r={strokeWidth * 0.75}
            fill="#ffffff"
            stroke={c.primary}
            strokeWidth={2}
            style={{
              filter: `drop-shadow(0 0 6px ${c.primary}) drop-shadow(0 0 10px #ffffff)`,
            }}
          />
        )}
      </svg>

      {/* 6. Central Suspended Spatial 3D Gold Coin */}
      <div
        className="relative z-20 flex items-center justify-center pointer-events-none"
        style={{
          transformStyle: 'preserve-3d',
        }}
      >
        {children ? (
          children
        ) : showCoin ? (
          <NovaCoin3D
            size={coinSize}
            animate={status === 'mining'}
            interactive={true}
            tiltX={tilt.x}
            tiltY={tilt.y}
            speed={status === 'claiming' ? 'fast' : 'normal'}
          />
        ) : null}
      </div>
    </div>
  );
};

